import { convergingPaths, idsToRoot, isParent } from "./id.js";
import { binarySearch } from "../lib/array.js";
import "./node.js";
import { emitInspectorMessage, inspectNode, subscribeToInspectorCommands } from "../inspector.js";
import { defaultHandler, executeHandler } from "../handler/handler.js";
import { notifyListeners } from "./events.js";
//#region src/tree/tree.ts
/**
* Creates a new navigation tree. Newly created trees contain only a single root node with {@link defaultHandler}.
*
* You can give the tree an optional `label`, used for identification in the inspector
* in case there are multiple trees.
*
* @see {@link NavigationTreeOptions}
* @see {@link NavigationTree}
*/
function createNavigationTree(options = {}) {
	const tree = {
		focus: "#",
		label: options.label ?? randomLabel(),
		nodes: /* @__PURE__ */ new Map(),
		orphans: /* @__PURE__ */ new Map(),
		listeners: /* @__PURE__ */ new Map(),
		focusLock: "free"
	};
	tree.nodes.set("#", {
		tree,
		id: "#",
		connected: true,
		parent: null,
		order: 0,
		handler: defaultHandler,
		children: []
	});
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) subscribeToInspectorCommands(tree, (cmd) => handleInspectorCommand(tree, cmd));
	return tree;
}
/**
* Insert node created by `createNode` in the tree.
*
* @param tree - navigation tree
* @param node - node to insert
* @return A cleanup function that removes the node
*
* Nodes can be inserted before their parents exist. In that case they are kept in a
* disconnected state (not participating in navigation) until their parent is inserted.
* Similarly, upon parent removal they go back to a disconnected state and must be removed
* explicitly.
*
* When a tree already contains a node with the same id, it is replaced by the new node.
*
* @see {@link NavigationNode}
* @see {@link removeNode}
*/
function insertNode(tree, node) {
	if (node.parent === null) throw new Error("trying to insert root (or node without parent)");
	if (tree.nodes.has(node.id)) removeNode(tree, node.id);
	node.tree = tree;
	const insertedNode = node;
	tree.nodes.set(node.id, insertedNode);
	const parentNode = tree.nodes.get(node.parent);
	if (parentNode != null && parentNode.connected) connectNode(tree, parentNode, insertedNode);
	else markOrphan(tree, node.parent, node.id);
	return () => {
		removeNode(tree, insertedNode);
	};
}
function connectNode(tree, parentNode, node) {
	insertChildInOrder(parentNode, node);
	node.connected = true;
	if (isParent(tree.focus, node.id)) updateFocus(tree);
	const orphans = tree.orphans.get(node.id);
	if (orphans != null) {
		for (const orphanId of orphans) connectNode(tree, node, tree.nodes.get(orphanId));
		tree.orphans.delete(node.id);
	}
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) emitInspectorMessage({
		type: "fiveway:treeState",
		tree: tree.label,
		nodes: [inspectNode(node), inspectNode(parentNode)]
	});
}
/**
* Removes a node by id or by reference.
*
* @param tree - navigation tree
* @param node - accepts either the node id or the node itself
*
* Instead of using this function directly, prefer using removal function returned from {@link insertNode}
* Removing a node does not remove its children — they are put into a disconnected state
* until they are explicitly removed or their parent is connected again.
*
* This function is mainly meant to be used by framework integrations.
*/
function removeNode(tree, node) {
	const id = typeof node === "string" ? node : node.id;
	if (id === "#") throw new Error("cannot remove root node");
	const existingNode = tree.nodes.get(id);
	if (existingNode == null) return;
	if (typeof node !== "string" && node !== existingNode) return;
	if (existingNode.connected) disconnectNode(tree, id);
	tree.nodes.delete(id);
	clearOrphan(tree, existingNode.parent, id);
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) {
		const parentNode = tree.nodes.get(existingNode.parent);
		emitInspectorMessage({
			type: "fiveway:treeState",
			tree: tree.label,
			nodes: parentNode != null ? [inspectNode(parentNode)] : void 0,
			removedNodes: [id]
		});
	}
	if (isFocused(tree, existingNode.id)) {
		tree.focus = existingNode.parent ?? "#";
		updateFocus(tree);
	}
}
function disconnectNode(tree, nodeId) {
	const node = tree.nodes.get(nodeId);
	if (node == null) return;
	if (node.parent == null) throw new Error("trying to disconnect invalid node");
	for (const child of node.children) {
		if (!child.active) continue;
		disconnectNode(tree, child.id);
		markOrphan(tree, node.id, child.id);
	}
	const parentNode = tree.nodes.get(node.parent);
	if (parentNode != null) removeChildFromParent(parentNode, node);
	node.connected = false;
}
function updateFocus(tree) {
	if (tree.focusLock !== "free") {
		tree.focusLock = "updatePending";
		return;
	}
	const focusedNode = tree.nodes.get(tree.focus);
	if (focusedNode == null || !focusedNode.connected) idsToRoot(tree.focus, (id) => {
		if (focusNode(tree, id, { direction: "initial" })) return false;
	});
	else focusNode(tree, focusedNode.id, { direction: "initial" });
}
/**
* Temporarily locks focus from changing while the tree structure changes to allow
* inserting multiple nodes at once to resolve initial focus correctly.
*
* @param tree - navigation tree to hold the focus on
* @return A function that releases the lock. If a lock is already held, returns `null`.
*
* Calling the release function may apply a pending focus update.
*
* Used by framework integrations to make initial focus work in frameworks that run
* effects top-down.
*/
function holdFocus(tree) {
	if (tree.focusLock !== "free") return null;
	tree.focusLock = "locked";
	return () => {
		if (tree.focusLock === "free") throw new Error("trying to release focus lock without holding it");
		const updatePending = tree.focusLock === "updatePending";
		tree.focusLock = "free";
		if (updatePending) updateFocus(tree);
	};
}
/**
* Attempts to focus `targetId`.
*
* @param tree - navigation tree
* @param targetId - ID of the node to focus
* @param options - Options for the focus operation
* @return `true` if focus ends up on the target node.
*
* Calling `focusNode` dispatches a {@link FocusAction}, so focus is resolved via handlers.
* For example if a container contains children, calling `focusNode(tree, "#/container")`
* will typically focus a descendant like `#/container/item1`.
*
* @see {@link FocusNodeOptions}
*/
function focusNode(tree, targetId, options = {}) {
	const node = tree.nodes.get(targetId);
	if (node == null || !node.connected) return false;
	const focusAction = {
		kind: "focus",
		direction: options.direction ?? null
	};
	const resolvedId = executeHandler(node, focusAction);
	if (resolvedId === null) return false;
	if (tree.focus === resolvedId) return true;
	const lastFocused = tree.focus;
	tree.focus = resolvedId;
	convergingPaths(lastFocused, tree.focus, (id) => {
		notifyListeners(tree, id);
	});
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) emitInspectorMessage({
		type: "fiveway:treeState",
		tree: tree.label,
		focus: tree.focus
	});
	return true;
}
/**
* Dispatches {@link NavigationAction} to focused or specified node handler.
* If handler returns an ID, focus is updated to that ID.
*
* @param tree - navigation tree
* @param action - navigation action to dispatch
* @param target - ID to dispatch the action on. If not specified, the focused node is used.
*/
function dispatchAction(tree, action, target) {
	const node = tree.nodes.get(target ?? tree.focus);
	if (node == null || !node.connected) return;
	const focusId = executeHandler(node, action);
	if (focusId !== null) focusNode(tree, focusId);
}
/**
* Checks whether `nodeId` is focused. A node is also considered focused when its descendant is focused.
*
* @param focused - tree or id of a focused node
* @param nodeId - id being checked for focus
* @return `true` if the node or its descendant is focused
*
* To be efficient and flexible this function produces results just by checking the path of node IDs without traversing the tree itself.
* That means it won't error when given node ID doesn't exist in the tree.
*/
function isFocused(focused, nodeId) {
	const focusedId = typeof focused === "string" ? focused : focused.focus;
	return focusedId === nodeId || isParent(nodeId, focusedId);
}
/**
* Traverses nodes under given node up to specified depth and calls a callback for each of them.
*
* @param tree - navigation tree
* @param nodeId - ID of the node to traverse
* @param depth - The depth up to which nodes are traversed. `null` means no limit.
* @param callback - callback called for each node with nodes' ID as its only argument
*/
function traverseNodes(tree, nodeId, depth, callback) {
	if (depth === 0) return;
	const node = tree.nodes.get(nodeId);
	if (node == null || !node.connected) return;
	for (const child of node.children) if (child.active) {
		callback(child.id);
		traverseNodes(tree, child.id, depth !== null ? depth - 1 : null, callback);
	}
}
function insertChildInOrder(parentNode, childNode) {
	const oldIndex = parentNode.children.findIndex((child) => child.id === childNode.id);
	if (oldIndex !== -1) {
		if (childNode.order === null) {
			parentNode.children[oldIndex].active = true;
			return;
		}
		parentNode.children.splice(oldIndex, 1);
	}
	const newIndex = binarySearch(parentNode.children, (child) => (childNode.order ?? 0) < (child.order ?? 0));
	parentNode.children.splice(newIndex, 0, {
		active: true,
		id: childNode.id,
		order: childNode.order
	});
}
function removeChildFromParent(parentNode, childNode) {
	let parentChildIndex = parentNode.children.findIndex((child) => child.id === childNode.id);
	if (parentChildIndex === -1) {
		console.error("encountered broken tree");
		return;
	}
	if (childNode.order === null) parentNode.children[parentChildIndex].active = false;
	else parentNode.children.splice(parentChildIndex, 1);
}
function markOrphan(tree, parent, child) {
	if (!tree.orphans.has(parent)) tree.orphans.set(parent, []);
	tree.orphans.get(parent).push(child);
}
function clearOrphan(tree, parent, child) {
	const orphans = tree.orphans.get(parent);
	if (orphans != null) {
		const index = orphans.indexOf(child);
		if (index !== -1) orphans.splice(index, 1);
	}
}
function handleInspectorCommand(tree, command) {
	if (command.kind === "dispatchAction") dispatchAction(tree, command.action, command.node);
	if (command.kind === "inspectHandler") {
		const node = tree.nodes.get(command.node);
		if (node == null || !node.connected) return;
		emitInspectorMessage({
			type: "fiveway:treeState",
			tree: tree.label,
			nodes: [inspectNode(node, true)]
		});
	}
	if (command.kind === "requestCompleteSnapshot") {
		const nodes = Array.from(tree.nodes.values(), (node) => inspectNode(node, false));
		emitInspectorMessage({
			type: "fiveway:treeState",
			tree: tree.label,
			focus: tree.focus,
			nodes,
			complete: true
		});
	}
}
function randomLabel() {
	return `${Math.random().toString(36).substring(2, 15)}`;
}
//#endregion
export { createNavigationTree, dispatchAction, focusNode, holdFocus, insertNode, isFocused, removeNode, traverseNodes };

//# sourceMappingURL=tree.js.map